import { Navigate } from "react-router-dom";
import type { ReactNode } from "react";
import { useAppSelector } from "@/app/hooks";
import {
  selectIsAuthenticated,
  selectToken,
  selectUser,
} from "@/features/auth/authSlice";
import { ROLE_RENTER } from "@/types/auth";

interface DashboardAccessGuardProps {
  children: ReactNode;
}

function DashboardAccessLoading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="text-center">
        <svg
          className="animate-spin h-10 w-10 text-teal-600 mx-auto"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
        </svg>
        <p className="mt-4 text-sm text-gray-600">Loading...</p>
      </div>
    </div>
  );
}

/**
 * Prevent renter/applicant sessions from entering the admin dashboard shell.
 *
 * Show a loading shell only when we have a token but no user yet (initial hydrate).
 * Do **not** unmount the dashboard while `fetchCurrentUser` refreshes an existing user —
 * that remount wipes in-progress wizard state (e.g. transfer Residents → Summary).
 */
export function DashboardAccessGuard({ children }: DashboardAccessGuardProps) {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const token = useAppSelector(selectToken);
  const user = useAppSelector(selectUser);

  if (!isAuthenticated) {
    return <>{children}</>;
  }

  if (user?.current_role?.role_name === ROLE_RENTER) {
    return <Navigate to="/applicant/dashboard" replace />;
  }

  // Block only until first user payload arrives — not on subsequent profile refreshes
  // (`fetchCurrentUser` sets isLoading and previously remounted the whole dashboard).
  if (token && !user) {
    return <DashboardAccessLoading />;
  }

  return <>{children}</>;
}
